import { useCallback, useEffect, useState } from "react";
import "./TidyPage.css";
import "./AlarmsPage.css";
import { AppShell, SideCheck, SideGroup } from "../app/AppShell";
import { AlarmListState, AlarmTable, useAlarmPick } from "../components/Alarm";
import { Button } from "../components/Button";
import { Alert } from "../components/Card";
import { PageHead } from "../components/Page";
import { Chip } from "../components/Status";
import { ALARMS_CHANGED, KIND_KEYS, KIND_KO, RANGE_KEYS, alarmApi, alarmsChanged, sinceOf } from "../lib/alarms";
import type { Alarm, AlarmKind, AlarmRange } from "../lib/alarms";
import { Input } from "../components/Field";
import { Icon } from "../components/Icon";
import { errText } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 알람 화면 #/alarms ══
   채택 시안 **안 A**(Figma 58-3 ②, 사람 확정 2026-10-01). 종 팝오버의 「전체 보기」가 여기로
   온다 — 내비 링크는 안 더한다(안 읽은 수를 든 자리는 종 하나다). 읽는 문은 `GET /alarms`,
   쓰는 문은 `POST /alarms/read` 하나(`crates/api/src/alarm.rs`). 왜 이 모양인지는
   `docs/decisions/alarm.md`.

   거르개(안 읽은 것만 · 갈래 · 기간 · 대상 이슈 — 기간과 이슈는 t-in-port-obs-alarms 가 더했다)는 **서버 거르개**다 — 목록이 쪽으로 오기 때문에 화면이 접으면
   「더 보기」 뒤의 줄이 빠진다. 바꾸면 처음부터 다시 받고 선택을 비운다. */

/** 갈래를 전부 고른 것은 「거르지 않음」이다 — 서버에 갈래를 안 보낸다. 서버가 갈래를 늘린 날
 *  새 갈래가 이 화면에서 사라지지 않게. */
function kindParamOf(kinds: Set<AlarmKind>): string[] {
  return kinds.size === KIND_KEYS.length ? [] : [...kinds];
}

export function AlarmsPage() {
  const [items, setItems] = useState<Alarm[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [kinds, setKinds] = useState<Set<AlarmKind>>(new Set(KIND_KEYS));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [range, setRange] = useState<AlarmRange>("all");
  // 이슈 칸은 친 대로 바로 거르지 않는다 — Enter · 칸을 벗어날 때 적용한다(글자마다 다시 받지 않게).
  const [taskDraft, setTaskDraft] = useState("");
  const [task, setTask] = useState("");

  /** 기간의 시작 시각은 **받을 때마다** 지금 기준으로 다시 센다 — 「더 보기」도 같은 값을 쓴다. */
  const [since, setSince] = useState<string | null>(null);
  const query = useCallback(() => ({ kinds: kindParamOf(kinds), unread: onlyUnread, since, task: task || null }), [kinds, onlyUnread, since, task]);

  const load = useCallback(async () => {
    setSelected(new Set());
    if (kinds.size === 0) {
      setItems([]);
      setNext(null);
      setTotal(0);
      setLoadErr(null);
      return;
    }
    try {
      const page = await alarmApi.list(query());
      setItems(page.items);
      setNext(page.nextBefore);
      setTotal(page.total);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(errText(e));
    }
  }, [kinds, query]);

  useEffect(() => {
    load();
  }, [load]);

  /* 종에서 「모두 읽음」을 누르면 이 화면의 ● 도 맞아야 한다. */
  useEffect(() => {
    const on = () => {
      load();
    };
    addEventListener(ALARMS_CHANGED, on);
    return () => removeEventListener(ALARMS_CHANGED, on);
  }, [load]);

  async function loadMore() {
    if (!next) return;
    setMore(true);
    try {
      const page = await alarmApi.list({ ...query(), before: next });
      setItems((xs) => [...(xs ?? []), ...page.items]);
      setNext(page.nextBefore);
      setTotal(page.total);
    } catch (e) {
      setActionErr(errText(e));
    } finally {
      setMore(false);
    }
  }

  /** 고른 줄을 읽음으로. 끝나면 종과 이 화면이 같은 신호(`alarmsChanged`)로 다시 읽는다 —
   *  낙관적으로 ● 를 지우지 않는다. */
  async function markSelected() {
    setBusy(true);
    setActionErr(null);
    try {
      await alarmApi.read([...selected]);
      alarmsChanged();
    } catch (e) {
      setActionErr(errText(e));
    } finally {
      setBusy(false);
    }
  }

  async function markAll() {
    setBusy(true);
    setActionErr(null);
    try {
      await alarmApi.readAll();
      alarmsChanged();
    } catch (e) {
      setActionErr(errText(e));
    } finally {
      setBusy(false);
    }
  }

  const pick = useAlarmPick();

  function toggleKind(k: AlarmKind) {
    setKinds((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  }
  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  function toggleAll() {
    const ids = (items ?? []).map((a) => a.id);
    setSelected((s) => (ids.length && ids.every((id) => s.has(id)) ? new Set() : new Set(ids)));
  }

  function pickRange(r: AlarmRange) {
    setRange(r);
    setSince(sinceOf(r, Date.now()));
  }
  function applyTask() {
    const v = taskDraft.trim();
    if (v !== task) setTask(v);
  }
  /** 다시 불러오기 — 기간의 시작도 지금 기준으로 다시 센다(「오늘」이 어제에 머물지 않게). */
  function reload() {
    const s = sinceOf(range, Date.now());
    if (s !== since) setSince(s);
    else void load();
  }

  const filtered = onlyUnread || kinds.size !== KIND_KEYS.length || range !== "all" || !!task;
  const side = (
    <>
      <SideGroup>{tx("alarm.page.view")}</SideGroup>
      <SideCheck checked={onlyUnread} onChange={setOnlyUnread}>
        {tx("alarm.page.onlyUnread")}
      </SideCheck>
      <SideGroup>{tx("alarm.table.kind")}</SideGroup>
      {KIND_KEYS.map((k) => (
        <SideCheck key={k} checked={kinds.has(k)} onChange={() => toggleKind(k)}>
          {KIND_KO[k]}
        </SideCheck>
      ))}
    </>
  );

  const rows = items ?? [];
  return (
    <AppShell route="/alarms" sidebar={side} sidebarTitle={tx("alarm.page.menu")}>
      <div className="alarms">
        <PageHead
          title={tx("alarm.title")}
          meta={<Chip tone="muted">{total === null ? "…" : tx("alarm.page.count", { n: total })}</Chip>}
          actions={
            <Button size="md" variant="ghost" onClick={markAll} disabled={busy || !rows.some((a) => !a.read)}>
              {tx("alarm.readAll")}
            </Button>
          }
        />
        <div className="b-filterbar alarms__filters">
          <div className="b-filterbar-chips" role="group" aria-label={tx("alarm.page.range")}>
            {RANGE_KEYS.map((r) => (
              <button key={r} type="button" className={`b-chip${range === r ? " is-on" : ""}`} aria-pressed={range === r} onClick={() => pickRange(r)}>
                {tx(`alarm.page.rangeOf.${r}`)}
              </button>
            ))}
          </div>
          <div className="b-filterbar-search">
            <Input
              size="md"
              value={taskDraft}
              placeholder={tx("alarm.page.taskPh")}
              aria-label={tx("alarm.page.task")}
              onChange={(e) => setTaskDraft(e.target.value)}
              onBlur={applyTask}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyTask();
              }}
            />
          </div>
          <div className="b-filterbar-end">
            <Button size="md" variant="ghost" onClick={reload}>
              <Icon name="refresh" /> {tx("alarm.page.reload")}
            </Button>
          </div>
        </div>
        <p className="alarms__hint">
          <Icon name="info" /> {tx("alarm.page.hint")}
        </p>
        {actionErr ? <Alert tone="error">{actionErr}</Alert> : null}
        {loadErr || items === null || !rows.length ? (
          <AlarmListState err={loadErr} loading={items === null && !loadErr} empty={items !== null && !rows.length} filtered={filtered} onRetry={load} />
        ) : (
          <>
            <AlarmTable
              rows={rows}
              selected={selected}
              now={Date.now()}
              busy={busy}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onPick={pick}
              onRead={markSelected}
              onClear={() => setSelected(new Set())}
            />
            <p className="alarms__more">
              {total !== null && total > rows.length ? (
                <>
                  {tx("alarm.page.partial", { total, n: rows.length })}{" "}
                  <Button size="sm" variant="ghost" onClick={loadMore} loading={more} disabled={!next}>
                    {tx("alarm.page.loadMore")}
                  </Button>
                </>
              ) : (
                tx("alarm.page.end", { n: rows.length })
              )}
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
}
